import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Typography, Paper, Box, CircularProgress, Button, TextField, MenuItem, FormControlLabel, Switch, Stack } from '@mui/material';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import http from '../../services/http';

// Phase 1 structural alignment (no regression):
// - Keep this page as the detailed census/reporting surface
// - Reposition it clearly under Comprehensive Services rather than as a parallel launch hub
// - Preserve all charts, tables, export controls, pagination, and fallback aggregation logic

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

function getTenantSlug() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/([^/]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  } catch {
    return '';
  }
}

function tenantUrl(path) {
  const slug = getTenantSlug();
  const p = String(path || '');
  if (!slug) return p;
  if (!p.startsWith('/')) return `/t/${slug}/${p}`.replace(/\/{2,}/g, '/');
  if (p.startsWith(`/t/${slug}`)) return p;
  return `/t/${slug}${p}`.replace(/\/{2,}/g, '/');
}

const emptyBuckets = {
  byDisease: {},
  bySex: {},
  byAgeGroup: {},
  byVisitType: {},
  byCentre: {},
  byActivityType: {},
  byActivityStatus: {},
};

const AGE_GROUP_ORDER = [
  'Newborn',
  'Infant',
  'Under-5',
  '6-9 years',
  '10-19 years',
  '20-39 years',
  '40-59 years',
  '60+ years',
  'Unknown',
];

const OFFICIAL_CENTRES = [
  'CRHA Dhundhara',
  'CRHA Dhawa',
  'CRHA Keru',
  'UHTC Pratap Nagar',
];

const LEGACY_CENTRE_BUCKET = 'Legacy / Early-stage Records';

const EXPORT_FORMAT_OPTIONS = ['CSV', 'JSON', 'Print / PDF'];
const EXPORT_SCOPE_OPTIONS = ['Summary Only', 'Full Census'];
const EXPORT_DETAIL_RANGE_OPTIONS = ['Same Day Only', 'Selected Period'];
const DEFAULT_TABLE_PAGE_SIZE = 10;

function normalizeList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.visits)) return data.visits;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.activities)) return data.activities;
  return [];
}

function increment(bucket, key, amount = 1) {
  const normalizedKey = String(key || 'Unknown').trim() || 'Unknown';
  bucket[normalizedKey] = (bucket[normalizedKey] || 0) + Number(amount || 0);
}

function normalizeSex(value) {
  const v = String(value || '').trim().toLowerCase();
  if (!v) return 'Unknown';
  if (['m', 'male'].includes(v)) return 'Male';
  if (['f', 'female'].includes(v)) return 'Female';
  if (['other', 'others', 'transgender'].includes(v)) return 'Other';
  return String(value || 'Unknown').trim() || 'Unknown';
}

function normalizeCentre(value) {
  const original = String(value || '').trim();
  const v = original.toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

  if (!v) return LEGACY_CENTRE_BUCKET;
  if (v.includes('keru')) return 'CRHA Keru';
  if (v.includes('dhawa')) return 'CRHA Dhawa';
  if (v.includes('pratap')) return 'UHTC Pratap Nagar';
  if (
    v.includes('dhundhara') ||
    v.includes('dundhara') ||
    v.includes('dujdhara') ||
    v.includes('dundara') ||
    v.includes('dhundara')
  ) {
    return 'CRHA Dhundhara';
  }

  if (['unknown', 'test', 'na', 'n/a', 'null', 'undefined', '-'].includes(v)) {
    return LEGACY_CENTRE_BUCKET;
  }

  return LEGACY_CENTRE_BUCKET;
}

function normalizeVisitType(value) {
  const v = String(value || '').trim().toLowerCase();
  if (!v) return 'General';
  if (v.includes('antenatal') || v === 'anc') return 'Antenatal';
  if (v.includes('postnatal') || v === 'pnc') return 'Postnatal';
  if (v.includes('follow')) return 'Follow-up';
  if (v.includes('new')) return 'New';
  if (v.includes('emergency')) return 'Emergency';
  return String(value || 'General').trim() || 'General';
}

function normalizeActivityType(item) {
  return (
    item?.templateTitle ||
    item?.templateName ||
    item?.activityType ||
    item?.type ||
    item?.template?.title ||
    item?.template?.name ||
    item?.title ||
    item?.name ||
    'Unspecified Activity'
  );
}

function normalizeActivityStatus(item) {
  const raw = String(
    item?.status ||
      item?.approvalStatus ||
      item?.workflowStatus ||
      item?.reviewStatus ||
      ''
  ).trim();
  return raw || 'Unknown';
}

function ageToGroup(ageValue) {
  const age = Number(ageValue);
  if (!Number.isFinite(age) || age < 0) return 'Unknown';
  if (age < 0.08) return 'Newborn';
  if (age < 1) return 'Infant';
  if (age < 5) return 'Under-5';
  if (age <= 9) return '6-9 years';
  if (age <= 19) return '10-19 years';
  if (age <= 39) return '20-39 years';
  if (age <= 59) return '40-59 years';
  return '60+ years';
}

function extractDiseaseLabel(visit) {
  return (
    visit?.finalDiagnosis ||
    visit?.diagnosis ||
    visit?.provisionalDiagnosis ||
    visit?.chiefComplaint ||
    visit?.presentingComplaint ||
    visit?.complaint ||
    visit?.disease ||
    'Unspecified'
  );
}

function extractVisitDate(visit) {
  return visit?.visitDate || visit?.date || visit?.createdAt || visit?.updatedAt || null;
}

function extractActivityDate(item) {
  return (
    item?.activityDate ||
    item?.performedOn ||
    item?.date ||
    item?.createdAt ||
    item?.updatedAt ||
    null
  );
}

function toLocalYmd(dateValue) {
  if (!dateValue) return '';
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

function isWithinDateRange(dateValue, fromDate, toDate) {
  const normalizedDate = toLocalYmd(dateValue);
  if (!normalizedDate) return false;
  if (fromDate && normalizedDate < fromDate) return false;
  if (toDate && normalizedDate > toDate) return false;
  return true;
}

function isWithinSingleDay(dateValue, targetDate) {
  const normalizedDate = toLocalYmd(dateValue);
  if (!normalizedDate || !targetDate) return false;
  return normalizedDate === targetDate;
}

function sortAgeGroupEntries(entries) {
  const indexMap = new Map(AGE_GROUP_ORDER.map((item, index) => [item, index]));
  return [...entries].sort((a, b) => {
    const aIndex = indexMap.has(a[0]) ? indexMap.get(a[0]) : Number.MAX_SAFE_INTEGER;
    const bIndex = indexMap.has(b[0]) ? indexMap.get(b[0]) : Number.MAX_SAFE_INTEGER;
    if (aIndex !== bIndex) return aIndex - bIndex;
    return String(a[0]).localeCompare(String(b[0]));
  });
}

function makeBarData(label, entries, backgroundColor) {
  return {
    labels: entries.map(([key]) => key),
    datasets: [
      {
        label,
        data: entries.map(([, value]) => value),
        backgroundColor,
      },
    ],
  };
}

function makePieData(label, entries) {
  return {
    labels: entries.map(([key]) => key),
    datasets: [
      {
        label,
        data: entries.map(([, value]) => value),
        backgroundColor: [
          'rgba(66, 165, 245, 0.78)',
          'rgba(239, 83, 80, 0.78)',
          'rgba(158, 158, 158, 0.78)',
          'rgba(102, 187, 106, 0.78)',
          'rgba(171, 71, 188, 0.78)',
          'rgba(255, 167, 38, 0.78)',
        ],
      },
    ],
  };
}

function filterPositiveEntries(entries) {
  return entries.filter(([, value]) => Number(value) > 0);
}

function sortCentreEntries(entries) {
  const officialOrder = new Map([
    ['CRHA Dhundhara', 0],
    ['CRHA Dhawa', 1],
    ['CRHA Keru', 2],
    ['UHTC Pratap Nagar', 3],
    [LEGACY_CENTRE_BUCKET, 4],
  ]);

  return [...entries].sort((a, b) => {
    const aOrder = officialOrder.has(a[0]) ? officialOrder.get(a[0]) : 99;
    const bOrder = officialOrder.has(b[0]) ? officialOrder.get(b[0]) : 99;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return b[1] - a[1];
  });
}

function buildBarOptions({ horizontal = false } = {}) {
  const valueAxis = horizontal ? 'x' : 'y';
  const labelAxis = horizontal ? 'y' : 'x';

  return {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? 'y' : 'x',
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
    },
    scales: {
      [valueAxis]: {
        beginAtZero: true,
        ticks: {
          color: '#9ca3af',
          precision: 0,
          stepSize: 1,
        },
        grid: { color: 'rgba(255,255,255,0.06)' },
        border: { color: 'rgba(255,255,255,0.08)' },
      },
      [labelAxis]: {
        ticks: {
          color: '#9ca3af',
          autoSkip: false,
          maxRotation: horizontal ? 0 : 20,
          minRotation: 0,
        },
        grid: { display: false },
        border: { color: 'rgba(255,255,255,0.08)' },
      },
    },
  };
}

function buildPieOptions() {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: '#d1d5db', boxWidth: 14, padding: 14 },
      },
      tooltip: { enabled: true },
    },
  };
}

function MetricChartCard({
  title,
  subtitle,
  mode = 'bar',
  data,
  options,
  entries = [],
  compactLabel = 'Total',
}) {
  const safeEntries = filterPositiveEntries(entries);
  const topEntry = safeEntries[0] || null;

  return (
    <Paper className="p-4 shadow-md rounded-xl min-h-[360px]">
      <Typography variant="h6" className="mb-1">
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" className="mb-3 text-gray-500 dark:text-gray-400">
          {subtitle}
        </Typography>
      ) : null}

      {!safeEntries.length ? (
        <Box className="h-[260px] flex items-center justify-center text-gray-500 dark:text-gray-400">
          <Typography variant="body2">No data available for this chart.</Typography>
        </Box>
      ) : safeEntries.length === 1 ? (
        <Box className="h-[260px] flex flex-col items-center justify-center text-center gap-2">
          <Typography variant="body2" className="text-gray-500 dark:text-gray-400">
            {compactLabel}
          </Typography>
          <Typography variant="h3" className="font-semibold">
            {topEntry[1]}
          </Typography>
          <Typography variant="body1" className="text-gray-300 max-w-[80%]">
            {topEntry[0]}
          </Typography>
        </Box>
      ) : (
        <Box className="h-[280px]">
          {mode === 'pie' ? <Pie data={data} options={options} /> : <Bar data={data} options={options} />}
        </Box>
      )}
    </Paper>
  );
}

function extractPersonLabel(item) {
  return (
    item?.citizenName ||
    item?.patientName ||
    item?.patient ||
    item?.beneficiaryName ||
    item?.name ||
    item?.fullName ||
    item?.citizen?.name ||
    item?.contextName ||
    '-'
  );
}

function extractPersonId(item) {
  return (
    item?.citizenId ||
    item?.patientId ||
    item?.uhid ||
    item?.registrationNumber ||
    item?.citizen?._id ||
    item?.beneficiaryId ||
    item?.contextId ||
    '-'
  );
}

function extractCreatedBy(item) {
  return (
    item?.createdByName ||
    item?.userName ||
    item?.internName ||
    item?.doctorName ||
    item?.observerName ||
    item?.observer?.name ||
    item?.createdBy?.name ||
    item?.performedBy?.name ||
    item?.submittedByName ||
    '-'
  );
}

function filterActivityRows(rows, selectedCentre, fromDate, toDate) {
  return normalizeList(rows)
    .filter((item) => {
      const centre = normalizeCentre(item?.centre || item?.healthCentre || item?.location || item?.site);
      const matchesCentre = selectedCentre === 'All Centres' || centre === selectedCentre;
      const matchesDate = isWithinDateRange(extractActivityDate(item), fromDate, toDate);
      return matchesCentre && matchesDate;
    })
    .sort((a, b) => new Date(extractActivityDate(b) || 0) - new Date(extractActivityDate(a) || 0));
}

function filterVisitRows(rows, selectedCentre, fromDate, toDate) {
  return normalizeList(rows)
    .filter((visit) => {
      const visitCentre = normalizeCentre(visit?.centre || visit?.healthCentre || visit?.location);
      const matchesCentre = selectedCentre === 'All Centres' || visitCentre === selectedCentre;
      const matchesDate = isWithinDateRange(extractVisitDate(visit), fromDate, toDate);
      return matchesCentre && matchesDate;
    })
    .sort((a, b) => new Date(extractVisitDate(b) || 0) - new Date(extractVisitDate(a) || 0));
}

function activityRowSignature(item) {
  const stableId =
    item?._id ||
    item?.id ||
    item?.instanceId ||
    item?.activityId ||
    item?.submissionId ||
    item?.logId ||
    '';
  const date = toLocalYmd(extractActivityDate(item)) || '';
  const centre = normalizeCentre(item?.centre || item?.healthCentre || item?.location || item?.site);
  const type = normalizeActivityType(item);
  const personId = extractPersonId(item);
  const createdBy = extractCreatedBy(item);
  const sourceHint = String(item?.source || item?.origin || item?.kind || '').trim();

  return [stableId, date, centre, type, personId, createdBy, sourceHint].join('||');
}

function dedupeActivityRows(rows) {
  const seen = new Set();
  return normalizeList(rows).filter((item) => {
    const signature = activityRowSignature(item);
    if (seen.has(signature)) return false;
    seen.add(signature);
    return true;
  });
}

function buildCensusBucketsFromRows(visits, activities) {
  const buckets = {
    byDisease: {},
    bySex: {},
    byAgeGroup: {},
    byVisitType: {},
    byCentre: {},
    byActivityType: {},
    byActivityStatus: {},
  };

  normalizeList(visits).forEach((visit) => {
    increment(buckets.byDisease, extractDiseaseLabel(visit));
    increment(buckets.bySex, normalizeSex(visit?.sex || visit?.gender));
    increment(buckets.byAgeGroup, ageToGroup(visit?.age));
    increment(buckets.byVisitType, normalizeVisitType(visit?.visitType || visit?.type));
    increment(buckets.byCentre, normalizeCentre(visit?.centre || visit?.healthCentre || visit?.location));
  });

  normalizeList(activities).forEach((item) => {
    increment(buckets.byActivityType, normalizeActivityType(item));
    increment(buckets.byActivityStatus, normalizeActivityStatus(item));
    increment(
      buckets.byCentre,
      normalizeCentre(item?.centre || item?.healthCentre || item?.location || item?.site)
    );
  });

  return buckets;
}

function paginateRows(rows, page, pageSize) {
  const safePage = Math.max(1, Number(page) || 1);
  const startIndex = (safePage - 1) * pageSize;
  return rows.slice(startIndex, startIndex + pageSize);
}

function tablePaperTitle(title, subtitle) {
  return (
    <Box className="mb-3">
      <Typography variant="h6">{title}</Typography>
      {subtitle ? (
        <Typography variant="body2" className="text-gray-500 dark:text-gray-400">
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

function TablePaginationBar({ page, totalPages, onPrev, onNext, totalRows, pageSize, label }) {
  return (
    <Box className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <Typography variant="body2" className="text-gray-500 dark:text-gray-400">
        {label}: {totalRows} total • {pageSize} per page • Page {totalPages ? page : 0} of {totalPages}
      </Typography>
      <Box className="flex gap-2">
        <Button variant="outlined" size="small" onClick={onPrev} disabled={page <= 1 || totalPages === 0}>
          Previous
        </Button>
        <Button variant="outlined" size="small" onClick={onNext} disabled={page >= totalPages || totalPages === 0}>
          Next
        </Button>
      </Box>
    </Box>
  );
}

function classifyNcdBucket(visit) {
  const text = `${extractDiseaseLabel(visit)} ${visit?.chiefComplaint || ''} ${visit?.presentingComplaint || ''}`.toLowerCase();
  const hasDm = /(diabet|dm\b|sugar|hypergly)/i.test(text);
  const hasHtn = /(hypertension|htn\b|bp\b|blood pressure)/i.test(text);
  const hasThyroid = /(thyroid|hypothy|hyperthy)/i.test(text);
  const hasCopd = /(copd|asthma|bronchitis|chronic obstructive|wheeze)/i.test(text);

  if (hasDm && hasHtn) return 'DM + HTN';
  if (hasDm) return 'DM';
  if (hasHtn) return 'HTN';
  if (hasThyroid) return 'Thyroid';
  if (hasCopd) return 'COPD / Asthma';
  return 'Others';
}

function classifyServiceBucket(visit) {
  const text = `${extractDiseaseLabel(visit)} ${visit?.chiefComplaint || ''} ${visit?.presentingComplaint || ''}`.toLowerCase();
  const visitType = normalizeVisitType(visit?.visitType || visit?.type).toLowerCase();

  if (visitType.includes('antenatal') || /\banc\b|pregnan|antenatal/i.test(text)) return 'ANC';
  if (visitType.includes('postnatal') || /\bpnc\b|postnatal|post natal|lactat/i.test(text)) return 'PNC';
  if (/animal bite|dog bite|snake bite|bite wound|rabies/i.test(text)) return 'Animal Bite';
  if (/cough|cold|fever|respir|asthma|copd|throat|pneum/i.test(text)) return 'Respiratory';
  if (/abdomen|abdominal|diarrh|vomit|gastr|constipat|acidity|stool/i.test(text)) return 'Gastrointestinal';
  if (/skin|itch|fungal|rash|eczema|dermat/i.test(text)) return 'Skin';
  return 'Others';
}

function csvEscape(value) {
  const stringValue = String(value ?? '');
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

function downloadBlob(filename, content, type = 'text/plain;charset=utf-8') {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function createCsvSection(title, columns, rows) {
  const header = `${title}\n${columns.map(csvEscape).join(',')}`;
  const body = rows.map((row) => columns.map((column) => csvEscape(row[column] ?? '')).join(',')).join('\n');
  return `${header}\n${body}`.trim();
}

function BasicSummaryTable({ title, subtitle, columns, rows }) {
  return (
    <Paper className="p-4 shadow-md rounded-xl overflow-x-auto">
      {tablePaperTitle(title, subtitle)}
      <table className="min-w-full table-auto border-collapse border border-gray-300 dark:border-gray-600">
        <thead>
          <tr className="bg-gray-100 dark:bg-gray-700">
            {columns.map((column) => (
              <th key={column.key} className="border p-2 text-left whitespace-nowrap">
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((row, index) => (
              <tr key={`${title}-${index}`} className="text-sm">
                {columns.map((column) => (
                  <td key={column.key} className="border px-2 py-1 whitespace-nowrap">
                    {row[column.key] ?? '-'}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td className="border px-2 py-3 text-center" colSpan={columns.length}>
                No data available.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </Paper>
  );
}

const DailyOPDCensus = ({ navigate: injectedNavigate } = {}) => {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [censusData, setCensusData] = useState(emptyBuckets);
  const [sourceLabel, setSourceLabel] = useState('');
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [selectedCentre, setSelectedCentre] = useState('All Centres');
  const [visitRows, setVisitRows] = useState([]);
  const [activityRows, setActivityRows] = useState([]);
  const [visitPage, setVisitPage] = useState(1);
  const [activityPage, setActivityPage] = useState(1);
  const [exportFormat, setExportFormat] = useState('CSV');
  const [exportScope, setExportScope] = useState('Summary Only');
  const [showLegacyData, setShowLegacyData] = useState(false);
  const [includeDetailedExport, setIncludeDetailedExport] = useState(false);
  const [exportDetailRange, setExportDetailRange] = useState('Same Day Only');

  const goComprehensiveServices = useCallback(() => {
    if (typeof injectedNavigate === 'function') {
      injectedNavigate(tenantUrl('/dashboard/operations'));
      return;
    }
    window.location.href = tenantUrl('/dashboard/operations');
  }, [injectedNavigate]);

  const goReviewerWorkbench = useCallback(() => {
    if (typeof injectedNavigate === 'function') {
      injectedNavigate(tenantUrl('/dashboard/approval'));
      return;
    }
    window.location.href = tenantUrl('/dashboard/approval');
  }, [injectedNavigate]);

  const goCentreServicesHub = useCallback(() => {
    if (typeof injectedNavigate === 'function') {
      injectedNavigate(tenantUrl('/dashboard/operations/chc'));
      return;
    }
    window.location.href = tenantUrl('/dashboard/operations/chc');
  }, [injectedNavigate]);

  const buildFallbackCensus = useCallback(async () => {
    const sharedParams = {
      ...(fromDate ? { fromDate } : {}),
      ...(toDate ? { toDate } : {}),
      ...(selectedCentre && selectedCentre !== 'All Centres' ? { centre: selectedCentre } : {}),
      limit: 1000,
    };

    const [visitsRes, internActivitiesRes, activitiesRes, myActivitiesRes, internStatsRes, activityStatsRes] =
      await Promise.allSettled([
        http.get('/api/visits', { params: sharedParams }),
        http.get('/api/intern-activities', { params: sharedParams }),
        http.get('/api/activities', { params: sharedParams }),
        http.get('/api/activities/me', { params: sharedParams }),
        http.get('/api/intern-activities/stats', { params: sharedParams }),
        http.get('/api/activities/stats', { params: sharedParams }),
      ]);

    const visits = visitsRes.status === 'fulfilled' ? normalizeList(visitsRes.value?.data) : [];
    const internActivities =
      internActivitiesRes.status === 'fulfilled' ? normalizeList(internActivitiesRes.value?.data) : [];
    const guidedActivitiesAll =
      activitiesRes.status === 'fulfilled' ? normalizeList(activitiesRes.value?.data) : [];
    const guidedActivitiesMine =
      myActivitiesRes.status === 'fulfilled' ? normalizeList(myActivitiesRes.value?.data) : [];
    const guidedActivities = guidedActivitiesAll.length ? guidedActivitiesAll : guidedActivitiesMine;
    const internStats = internStatsRes.status === 'fulfilled' ? internStatsRes.value?.data || {} : {};
    const activityStats = activityStatsRes.status === 'fulfilled' ? activityStatsRes.value?.data || {} : {};

    const filteredVisits = filterVisitRows(visits, selectedCentre, fromDate, toDate);
    const combinedActivities = filterActivityRows(
      dedupeActivityRows([...internActivities, ...guidedActivities]),
      selectedCentre,
      fromDate,
      toDate
    );

    const buckets = buildCensusBucketsFromRows(filteredVisits, combinedActivities);

    const addStatsToBucket = (statsObject, bucket, prefix = '') => {
      if (!statsObject || typeof statsObject !== 'object') return;
      Object.entries(statsObject).forEach(([key, value]) => {
        const numeric = Number(value);
        if (!Number.isFinite(numeric) || numeric <= 0) return;
        increment(bucket, prefix ? `${prefix}: ${key}` : key, numeric);
      });
    };

    if (!combinedActivities.length) {
      addStatsToBucket(internStats?.byType, buckets.byActivityType, 'Activity');
      addStatsToBucket(activityStats?.byTemplate, buckets.byActivityType, 'Guided Activity');
    }

    return {
      buckets,
      visits: filteredVisits,
      activities: combinedActivities,
      sourceLabel:
        filteredVisits.length || combinedActivities.length
          ? 'Fallback from visits + activities for selected date range'
          : 'Stats fallback for selected date range',
    };
  }, [selectedCentre, fromDate, toDate]);

  const fetchData = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) setRefreshing(true);
      else setLoading(true);

      setError('');

      try {
        const params = new URLSearchParams();
        if (toDate) params.set('date', toDate);
        if (fromDate) params.set('fromDate', fromDate);
        if (toDate) params.set('toDate', toDate);
        if (selectedCentre && selectedCentre !== 'All Centres') params.set('centre', selectedCentre);
        const query = params.toString() ? `?${params.toString()}` : '';

        const res = await http.get(`/api/opd-census${query}`);
        const payload = res?.data || {};
        const nextData = {
          byDisease: payload?.byDisease || {},
          bySex: payload?.bySex || {},
          byAgeGroup: payload?.byAgeGroup || {},
          byVisitType: payload?.byVisitType || {},
          byCentre: payload?.byCentre || {},
          byActivityType: payload?.byActivityType || {},
          byActivityStatus: payload?.byActivityStatus || {},
        };

        const primaryVisits = filterVisitRows(normalizeList(payload?.visits), selectedCentre, fromDate, toDate);
        const primaryActivities = filterActivityRows(
          dedupeActivityRows(normalizeList(payload?.activities)),
          selectedCentre,
          fromDate,
          toDate
        );

        const hasPrimaryData = Object.values(nextData).some(
          (bucket) => bucket && typeof bucket === 'object' && Object.keys(bucket).length > 0
        );

        if (hasPrimaryData || primaryVisits.length || primaryActivities.length) {
          if (primaryActivities.length) {
            setVisitRows(primaryVisits);
            setActivityRows(primaryActivities);
            setCensusData(buildCensusBucketsFromRows(primaryVisits, primaryActivities));
            setSourceLabel('Primary OPD census API');
          } else {
            const fallback = await buildFallbackCensus();
            const mergedVisits = primaryVisits.length ? primaryVisits : fallback.visits;
            const mergedActivities = fallback.activities;
            setVisitRows(mergedVisits);
            setActivityRows(mergedActivities);
            setCensusData(buildCensusBucketsFromRows(mergedVisits, mergedActivities));
            setSourceLabel('Primary OPD census API + activity fallback');
          }
        } else {
          const fallback = await buildFallbackCensus();
          setCensusData(fallback.buckets);
          setVisitRows(fallback.visits);
          setActivityRows(fallback.activities);
          setSourceLabel(fallback.sourceLabel);
        }
      } catch (e) {
        try {
          const fallback = await buildFallbackCensus();
          setCensusData(fallback.buckets);
          setVisitRows(fallback.visits);
          setActivityRows(fallback.activities);
          setSourceLabel(fallback.sourceLabel);
        } catch {
          setError('Failed to fetch OPD census data.');
          setCensusData(emptyBuckets);
          setVisitRows([]);
          setActivityRows([]);
          setSourceLabel('');
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [buildFallbackCensus, selectedCentre, fromDate, toDate]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setVisitPage(1);
  }, [visitRows, fromDate, toDate, selectedCentre]);

  useEffect(() => {
    setActivityPage(1);
  }, [activityRows, fromDate, toDate, selectedCentre]);

  const {
    byDisease = {},
    bySex = {},
    byAgeGroup = {},
    byVisitType = {},
    byCentre = {},
    byActivityType = {},
    byActivityStatus = {},
  } = censusData || emptyBuckets;

  const diseaseEntries = useMemo(() => Object.entries(byDisease).sort((a, b) => b[1] - a[1]), [byDisease]);
  const sexEntries = useMemo(() => Object.entries(bySex).sort((a, b) => b[1] - a[1]), [bySex]);
  const ageEntries = useMemo(() => sortAgeGroupEntries(Object.entries(byAgeGroup)), [byAgeGroup]);
  const visitTypeEntries = useMemo(
    () => Object.entries(byVisitType).sort((a, b) => b[1] - a[1]),
    [byVisitType]
  );
  const centreEntries = useMemo(() => Object.entries(byCentre).sort((a, b) => b[1] - a[1]), [byCentre]);
  const activityTypeEntries = useMemo(
    () => Object.entries(byActivityType).sort((a, b) => b[1] - a[1]),
    [byActivityType]
  );
  const activityStatusEntries = useMemo(
    () => Object.entries(byActivityStatus).sort((a, b) => b[1] - a[1]),
    [byActivityStatus]
  );

  const pilotVisitRows = useMemo(
    () =>
      showLegacyData
        ? visitRows
        : visitRows.filter(
            (visit) => normalizeCentre(visit?.centre || visit?.healthCentre || visit?.location) !== LEGACY_CENTRE_BUCKET
          ),
    [showLegacyData, visitRows]
  );

  const pilotActivityRows = useMemo(
    () =>
      showLegacyData
        ? activityRows
        : activityRows.filter(
            (item) =>
              normalizeCentre(item?.centre || item?.healthCentre || item?.location || item?.site) !==
              LEGACY_CENTRE_BUCKET
          ),
    [activityRows, showLegacyData]
  );

  const pilotCentreEntries = useMemo(
    () =>
      showLegacyData
        ? centreEntries
        : centreEntries.filter(([centre]) => normalizeCentre(centre) !== LEGACY_CENTRE_BUCKET),
    [centreEntries, showLegacyData]
  );

  const totalVisitCases = useMemo(() => pilotVisitRows.length, [pilotVisitRows]);
  const totalActivities = useMemo(() => pilotActivityRows.length, [pilotActivityRows]);
  const totalCombined = totalVisitCases + totalActivities;
  const hasAnyData =
    diseaseEntries.length +
      sexEntries.length +
      ageEntries.length +
      visitTypeEntries.length +
      pilotCentreEntries.length +
      activityTypeEntries.length +
      activityStatusEntries.length +
      pilotVisitRows.length +
      pilotActivityRows.length >
    0;

  const dynamicCentreOptions = useMemo(() => {
    const hasLegacyBucket =
      Object.keys(byCentre || {}).some((centre) => normalizeCentre(centre) === LEGACY_CENTRE_BUCKET) ||
      visitRows.some((visit) => normalizeCentre(visit?.centre || visit?.healthCentre || visit?.location) === LEGACY_CENTRE_BUCKET) ||
      activityRows.some((item) => normalizeCentre(item?.centre || item?.healthCentre || item?.location || item?.site) === LEGACY_CENTRE_BUCKET);

    return [
      'All Centres',
      ...OFFICIAL_CENTRES,
      ...(showLegacyData && hasLegacyBucket ? [LEGACY_CENTRE_BUCKET] : []),
    ];
  }, [activityRows, byCentre, showLegacyData, visitRows]);

  useEffect(() => {
    if (!showLegacyData && selectedCentre === LEGACY_CENTRE_BUCKET) {
      setSelectedCentre('All Centres');
    }
  }, [selectedCentre, showLegacyData]);

  const diseaseChartEntries = useMemo(() => filterPositiveEntries(diseaseEntries).slice(0, 8), [diseaseEntries]);
  const sexChartEntries = useMemo(() => filterPositiveEntries(sexEntries), [sexEntries]);
  const ageChartEntries = useMemo(() => filterPositiveEntries(ageEntries), [ageEntries]);
  const visitTypeChartEntries = useMemo(() => filterPositiveEntries(visitTypeEntries), [visitTypeEntries]);
  const centreChartEntries = useMemo(
    () => sortCentreEntries(filterPositiveEntries(pilotCentreEntries)),
    [pilotCentreEntries]
  );
  const activityTypeChartEntries = useMemo(
    () => filterPositiveEntries(activityTypeEntries).slice(0, 8),
    [activityTypeEntries]
  );
  const activityStatusChartEntries = useMemo(
    () => filterPositiveEntries(activityStatusEntries),
    [activityStatusEntries]
  );

  const diseaseData = makeBarData('Cases by Disease', diseaseChartEntries, 'rgba(255, 159, 64, 0.78)');
  const sexData = makePieData('Cases by Sex', sexChartEntries);
  const ageData = makeBarData('Cases by Age Group', ageChartEntries, 'rgba(75, 192, 192, 0.78)');
  const visitTypeData = makeBarData('Cases by Visit Type', visitTypeChartEntries, 'rgba(126, 87, 194, 0.78)');
  const centreData = makeBarData('Cases by Centre', centreChartEntries, 'rgba(102, 187, 106, 0.78)');
  const activityTypeData = makeBarData(
    'Activities by Type',
    activityTypeChartEntries,
    'rgba(66, 165, 245, 0.78)'
  );
  const activityStatusData = makePieData('Activities by Status', activityStatusChartEntries);

  const diseaseBarOptions = useMemo(() => buildBarOptions({ horizontal: true }), []);
  const ageBarOptions = useMemo(() => buildBarOptions(), []);
  const visitTypeBarOptions = useMemo(() => buildBarOptions(), []);
  const activityTypeBarOptions = useMemo(() => buildBarOptions(), []);
  const centreBarOptions = useMemo(() => buildBarOptions({ horizontal: true }), []);
  const pieOptions = useMemo(() => buildPieOptions(), []);

  const combinedSummaryRows = useMemo(() => {
    const rows = [
      { category: 'OPD Visits', bucket: 'Total Visits', count: totalVisitCases },
      { category: 'Intern / Guided Activities', bucket: 'Total Activities', count: totalActivities },
      { category: 'Combined', bucket: 'Total Encounters + Activities', count: totalCombined },
      { category: 'Display Mode', bucket: 'Legacy / Historical Data', count: showLegacyData ? 'Shown' : 'Hidden' },
    ];

    visitTypeEntries.forEach(([label, count]) => {
      rows.push({ category: 'Visit Type', bucket: label, count });
    });

    activityTypeEntries.forEach(([label, count]) => {
      rows.push({ category: 'Activity Type', bucket: label, count });
    });

    activityStatusEntries.forEach(([label, count]) => {
      rows.push({ category: 'Activity Status', bucket: label, count });
    });

    return rows;
  }, [activityStatusEntries, activityTypeEntries, showLegacyData, totalActivities, totalCombined, totalVisitCases, visitTypeEntries]);

  const yearWiseCentreRows = useMemo(() => {
    const yearCentreCounts = {};
    const centreSet = new Set();

    pilotVisitRows.forEach((visit) => {
      const year = new Date(extractVisitDate(visit) || 0).getFullYear();
      if (!Number.isFinite(year) || year <= 1970) return;
      const centre = normalizeCentre(visit?.centre || visit?.healthCentre || visit?.location);
      const safeCentre = centre && centre !== 'Unknown' ? centre : 'Unknown';
      centreSet.add(safeCentre);
      if (!yearCentreCounts[year]) yearCentreCounts[year] = {};
      yearCentreCounts[year][safeCentre] = (yearCentreCounts[year][safeCentre] || 0) + 1;
    });

    const centres = Array.from(centreSet).sort((a, b) => a.localeCompare(b));
    return Object.keys(yearCentreCounts)
      .map(Number)
      .sort((a, b) => a - b)
      .map((year) => {
        const row = { year };
        centres.forEach((centre) => {
          row[centre] = yearCentreCounts[year][centre] || 0;
        });
        row.total = Object.values(yearCentreCounts[year]).reduce((sum, value) => sum + Number(value || 0), 0);
        return row;
      });
  }, [pilotVisitRows]);

  const yearWiseCentreColumns = useMemo(() => {
    const centreNames = Array.from(
      new Set(
        yearWiseCentreRows.flatMap((row) => Object.keys(row).filter((key) => !['year', 'total'].includes(key)))
      )
    ).sort((a, b) => a.localeCompare(b));
    return [
      { key: 'year', label: 'Year' },
      ...centreNames.map((centre) => ({ key: centre, label: centre })),
      { key: 'total', label: 'Total' },
    ];
  }, [yearWiseCentreRows]);

  const centreAgeRows = useMemo(() => {
    const centreAgeCounts = {};
    pilotVisitRows.forEach((visit) => {
      const centre = normalizeCentre(visit?.centre || visit?.healthCentre || visit?.location);
      const safeCentre = centre && centre !== 'Unknown' ? centre : 'Unknown';
      const ageGroup = ageToGroup(visit?.age);
      if (!centreAgeCounts[safeCentre]) centreAgeCounts[safeCentre] = {};
      centreAgeCounts[safeCentre][ageGroup] = (centreAgeCounts[safeCentre][ageGroup] || 0) + 1;
    });

    return Object.keys(centreAgeCounts)
      .sort((a, b) => a.localeCompare(b))
      .map((centre) => {
        const row = { centre };
        AGE_GROUP_ORDER.forEach((group) => {
          row[group] = centreAgeCounts[centre][group] || 0;
        });
        row.total = AGE_GROUP_ORDER.reduce((sum, group) => sum + Number(row[group] || 0), 0);
        return row;
      });
  }, [pilotVisitRows]);

  const centreAgeColumns = useMemo(
    () => [
      { key: 'centre', label: 'Centre' },
      ...AGE_GROUP_ORDER.map((group) => ({ key: group, label: group })),
      { key: 'total', label: 'Total' },
    ],
    []
  );

  const centreSexRows = useMemo(() => {
    const centreSexCounts = {};
    pilotVisitRows.forEach((visit) => {
      const centre = normalizeCentre(visit?.centre || visit?.healthCentre || visit?.location);
      const safeCentre = centre && centre !== 'Unknown' ? centre : 'Unknown';
      const sex = normalizeSex(visit?.sex || visit?.gender);
      if (!centreSexCounts[safeCentre]) centreSexCounts[safeCentre] = { Male: 0, Female: 0, Other: 0, Unknown: 0 };
      centreSexCounts[safeCentre][sex] = (centreSexCounts[safeCentre][sex] || 0) + 1;
    });

    return Object.keys(centreSexCounts)
      .sort((a, b) => a.localeCompare(b))
      .map((centre) => {
        const counts = centreSexCounts[centre];
        return {
          centre,
          Male: counts.Male || 0,
          Female: counts.Female || 0,
          Other: counts.Other || 0,
          Unknown: counts.Unknown || 0,
          total: (counts.Male || 0) + (counts.Female || 0) + (counts.Other || 0) + (counts.Unknown || 0),
        };
      });
  }, [pilotVisitRows]);

  const centreNcdRows = useMemo(() => {
    const centreNcdCounts = {};
    pilotVisitRows.forEach((visit) => {
      const centre = normalizeCentre(visit?.centre || visit?.healthCentre || visit?.location);
      const safeCentre = centre && centre !== 'Unknown' ? centre : 'Unknown';
      const bucket = classifyNcdBucket(visit);
      if (!centreNcdCounts[safeCentre]) {
        centreNcdCounts[safeCentre] = {
          DM: 0,
          HTN: 0,
          'DM + HTN': 0,
          Thyroid: 0,
          'COPD / Asthma': 0,
          Others: 0,
        };
      }
      centreNcdCounts[safeCentre][bucket] = (centreNcdCounts[safeCentre][bucket] || 0) + 1;
    });

    return Object.keys(centreNcdCounts)
      .sort((a, b) => a.localeCompare(b))
      .map((centre) => {
        const counts = centreNcdCounts[centre];
        return {
          centre,
          DM: counts.DM || 0,
          HTN: counts.HTN || 0,
          'DM + HTN': counts['DM + HTN'] || 0,
          Thyroid: counts.Thyroid || 0,
          'COPD / Asthma': counts['COPD / Asthma'] || 0,
          Others: counts.Others || 0,
          total:
            (counts.DM || 0) +
            (counts.HTN || 0) +
            (counts['DM + HTN'] || 0) +
            (counts.Thyroid || 0) +
            (counts['COPD / Asthma'] || 0) +
            (counts.Others || 0),
        };
      });
  }, [pilotVisitRows]);

  const centreServiceRows = useMemo(() => {
    const centreServiceCounts = {};
    pilotVisitRows.forEach((visit) => {
      const centre = normalizeCentre(visit?.centre || visit?.healthCentre || visit?.location);
      const safeCentre = centre && centre !== 'Unknown' ? centre : 'Unknown';
      const bucket = classifyServiceBucket(visit);
      if (!centreServiceCounts[safeCentre]) {
        centreServiceCounts[safeCentre] = {
          Respiratory: 0,
          Gastrointestinal: 0,
          Skin: 0,
          'Animal Bite': 0,
          ANC: 0,
          PNC: 0,
          Others: 0,
        };
      }
      centreServiceCounts[safeCentre][bucket] = (centreServiceCounts[safeCentre][bucket] || 0) + 1;
    });

    return Object.keys(centreServiceCounts)
      .sort((a, b) => a.localeCompare(b))
      .map((centre) => {
        const counts = centreServiceCounts[centre];
        return {
          centre,
          Respiratory: counts.Respiratory || 0,
          Gastrointestinal: counts.Gastrointestinal || 0,
          Skin: counts.Skin || 0,
          'Animal Bite': counts['Animal Bite'] || 0,
          ANC: counts.ANC || 0,
          PNC: counts.PNC || 0,
          Others: counts.Others || 0,
          total:
            (counts.Respiratory || 0) +
            (counts.Gastrointestinal || 0) +
            (counts.Skin || 0) +
            (counts['Animal Bite'] || 0) +
            (counts.ANC || 0) +
            (counts.PNC || 0) +
            (counts.Others || 0),
        };
      });
  }, [pilotVisitRows]);

  const visitTotalPages = Math.ceil(pilotVisitRows.length / DEFAULT_TABLE_PAGE_SIZE);

  const activityTotalPages = Math.ceil(pilotActivityRows.length / DEFAULT_TABLE_PAGE_SIZE);

  const paginatedVisitRows = useMemo(
    () => paginateRows(pilotVisitRows, visitPage, DEFAULT_TABLE_PAGE_SIZE),
    [pilotVisitRows, visitPage]
  );
  const paginatedActivityRows = useMemo(
    () => paginateRows(pilotActivityRows, activityPage, DEFAULT_TABLE_PAGE_SIZE),
    [pilotActivityRows, activityPage]
  );

  const exportSections = useMemo(() => {
    const sections = [
      {
        title: 'Filters',
        columns: ['From Date', 'To Date', 'Centre', 'Source'],
        rows: [
          {
            'From Date': fromDate || '',
            'To Date': toDate || '',
            Centre: selectedCentre || 'All Centres',
            Source: sourceLabel || '',
          },
        ],
      },
      {
        title: 'Combined Summary',
        columns: ['category', 'bucket', 'count'],
        rows: combinedSummaryRows,
      },
      {
        title: 'Year-wise Centre OPD Summary',
        columns: yearWiseCentreColumns.map((column) => column.key),
        rows: yearWiseCentreRows,
      },
      {
        title: 'Centre-wise Age Summary',
        columns: centreAgeColumns.map((column) => column.key),
        rows: centreAgeRows,
      },
      {
        title: 'Centre-wise Sex Summary',
        columns: ['centre', 'Male', 'Female', 'Other', 'Unknown', 'total'],
        rows: centreSexRows,
      },
      {
        title: 'Centre-wise NCD Summary',
        columns: ['centre', 'DM', 'HTN', 'DM + HTN', 'Thyroid', 'COPD / Asthma', 'Others', 'total'],
        rows: centreNcdRows,
      },
      {
        title: 'Centre-wise Service Category Summary',
        columns: ['centre', 'Respiratory', 'Gastrointestinal', 'Skin', 'Animal Bite', 'ANC', 'PNC', 'Others', 'total'],
        rows: centreServiceRows,
      },
    ];

    if (exportScope === 'Full Census' && includeDetailedExport) {
      const exportVisitRows =
        exportDetailRange === 'Same Day Only'
          ? pilotVisitRows.filter((visit) => isWithinSingleDay(extractVisitDate(visit), toDate))
          : pilotVisitRows;

      const exportActivityRows =
        exportDetailRange === 'Same Day Only'
          ? pilotActivityRows.filter((item) => isWithinSingleDay(extractActivityDate(item), toDate))
          : pilotActivityRows;

      sections.push(
        {
          title: 'OPD Visits Table',
          columns: ['date', 'visitId', 'citizen', 'diagnosis', 'visitType', 'centre'],
          rows: exportVisitRows.map((visit) => ({
            date: toLocalYmd(extractVisitDate(visit)) || '',
            visitId: visit?.visitId || visit?._id || '',
            citizen: `${extractPersonLabel(visit)} (${extractPersonId(visit)})`,
            diagnosis: extractDiseaseLabel(visit),
            visitType: normalizeVisitType(visit?.visitType || visit?.type),
            centre: normalizeCentre(visit?.centre || visit?.healthCentre || visit?.location),
          })),
        },
        {
          title: 'Activities Table',
          columns: ['date', 'activity', 'status', 'citizenOrContext', 'doneBy', 'centre'],
          rows: exportActivityRows.map((item) => ({
            date: toLocalYmd(extractActivityDate(item)) || '',
            activity: normalizeActivityType(item),
            status: normalizeActivityStatus(item),
            citizenOrContext: `${extractPersonLabel(item)} (${extractPersonId(item)})`,
            doneBy: extractCreatedBy(item),
            centre: normalizeCentre(item?.centre || item?.healthCentre || item?.location || item?.site),
          })),
        }
      );
    }

    return sections;
  }, [
    includeDetailedExport,
    exportDetailRange,
    pilotActivityRows,
    centreAgeColumns,
    centreAgeRows,
    centreNcdRows,
    centreServiceRows,
    centreSexRows,
    combinedSummaryRows,
    exportScope,
    fromDate,
    selectedCentre,
    showLegacyData,
    sourceLabel,
    toDate,
    pilotVisitRows,
    yearWiseCentreColumns,
    yearWiseCentreRows,
  ]);

  const handleExport = useCallback(() => {
    const dateSuffix = `${fromDate || 'start'}_to_${toDate || 'end'}`.replace(/[^\w-]+/g, '_');
    const centreSuffix = (selectedCentre || 'all_centres').replace(/[^\w-]+/g, '_');

    if (exportFormat === 'JSON') {
      const payload = {
        filters: {
          fromDate,
          toDate,
          centre: selectedCentre,
          sourceLabel,
          exportScope,
          includeDetailedExport,
          exportDetailRange,
          showLegacyData,
        },
        sections: exportSections,
      };
      downloadBlob(
        `opd_census_${centreSuffix}_${dateSuffix}.json`,
        JSON.stringify(payload, null, 2),
        'application/json;charset=utf-8'
      );
      return;
    }

    if (exportFormat === 'CSV') {
      const content = exportSections
        .map((section) => createCsvSection(section.title, section.columns, section.rows))
        .join('\n\n');
      downloadBlob(`opd_census_${centreSuffix}_${dateSuffix}.csv`, content, 'text/csv;charset=utf-8');
      return;
    }

    const html = `
      <html>
        <head>
          <title>OPD Census Export</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #222; }
            h1, h2 { margin-bottom: 8px; }
            p { margin: 4px 0 12px; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 24px; }
            th, td { border: 1px solid #999; padding: 6px 8px; font-size: 12px; text-align: left; }
            th { background: #f3f3f3; }
          </style>
        </head>
        <body>
          <h1>OPD Census Export</h1>
          <p><strong>From:</strong> ${fromDate || '-'}</p>
          <p><strong>To:</strong> ${toDate || '-'}</p>
          <p><strong>Centre:</strong> ${selectedCentre || 'All Centres'}</p>
          <p><strong>Source:</strong> ${sourceLabel || '-'}</p>
          ${exportSections
            .map(
              (section) => `
                <h2>${section.title}</h2>
                <table>
                  <thead>
                    <tr>${section.columns.map((column) => `<th>${column}</th>`).join('')}</tr>
                  </thead>
                  <tbody>
                    ${
                      section.rows.length
                        ? section.rows
                            .map(
                              (row) => `
                                <tr>
                                  ${section.columns.map((column) => `<td>${String(row[column] ?? '')}</td>`).join('')}
                                </tr>
                              `
                            )
                            .join('')
                        : `<tr><td colspan="${section.columns.length}">No data available.</td></tr>`
                    }
                  </tbody>
                </table>
              `
            )
            .join('')}
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (!printWindow) return;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }, [exportFormat, exportScope, exportSections, fromDate, selectedCentre, sourceLabel, toDate, includeDetailedExport, exportDetailRange, showLegacyData]);

  if (loading) {
    return (
      <Box className="flex justify-center p-10">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="p-6 bg-white dark:bg-[#1e1e1e] text-gray-800 dark:text-gray-100 min-h-screen">
        <Typography color="error" className="p-4">
          {error}
        </Typography>
        <Button variant="contained" onClick={() => fetchData()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box className="p-6 bg-white dark:bg-[#1e1e1e] text-gray-800 dark:text-gray-100 min-h-screen">
      <Paper className="p-4 md:p-5 shadow-md rounded-xl mb-6">
        <Box className="flex flex-col gap-5">
          <Box className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
            <Box className="xl:max-w-[58%]">
              <Typography
                variant="h5"
                gutterBottom
                className="text-orange-700 dark:text-orange-300 font-semibold leading-tight max-w-[16ch] sm:max-w-none"
              >
                OPD & Activity Census Reporting
              </Typography>
              <Typography variant="body2" className="text-gray-600 dark:text-gray-300 max-w-3xl">
                Use this page as the detailed reporting and export surface for OPD visits and intern activities. Comprehensive Services remains the main operational hub, while this page focuses on analysis, summaries, and export-ready census views.
              </Typography>
              {sourceLabel ? (
                <Typography variant="caption" className="mt-2 block text-gray-500 dark:text-gray-400">
                  Data source: {sourceLabel}
                </Typography>
              ) : null}
            </Box>

            <Stack direction="row" spacing={1} className="flex-wrap">
              <Button variant="outlined" size="small" onClick={goComprehensiveServices}>
                Comprehensive Services
              </Button>
              <Button variant="outlined" size="small" onClick={goReviewerWorkbench}>
                Reviewer Workbench
              </Button>
              <Button variant="outlined" size="small" onClick={goCentreServicesHub}>
                Centre Services Hub
              </Button>
            </Stack>
          </Box>

          <Box className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 xl:min-w-[640px] xl:max-w-[760px] w-full">
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              value={fromDate}
              onChange={(e) => {
                const nextFromDate = e.target.value;
                setFromDate(nextFromDate);
                if (toDate && nextFromDate && nextFromDate > toDate) {
                  setToDate(nextFromDate);
                }
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              value={toDate}
              onChange={(e) => {
                const nextToDate = e.target.value;
                setToDate(nextToDate);
                if (fromDate && nextToDate && nextToDate < fromDate) {
                  setFromDate(nextToDate);
                }
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              size="small"
              select
              label="Centre"
              value={selectedCentre}
              onChange={(e) => setSelectedCentre(e.target.value)}
            >
              {dynamicCentreOptions.map((centre) => (
                <MenuItem key={centre} value={centre}>
                  {centre}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              onClick={() => fetchData()}
              disabled={refreshing}
              className="min-h-[40px] whitespace-nowrap"
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box className="grid grid-cols-1 md:grid-cols-5 gap-4 my-6">
        <Paper className="p-4 shadow-md rounded-xl">
          <Typography variant="body2" className="text-gray-500 dark:text-gray-400">
            OPD Visits
          </Typography>
          <Typography variant="h5" className="font-semibold">
            {totalVisitCases}
          </Typography>
        </Paper>
        <Paper className="p-4 shadow-md rounded-xl">
          <Typography variant="body2" className="text-gray-500 dark:text-gray-400">
            Activities
          </Typography>
          <Typography variant="h5" className="font-semibold">
            {totalActivities}
          </Typography>
        </Paper>
        <Paper className="p-4 shadow-md rounded-xl">
          <Typography variant="body2" className="text-gray-500 dark:text-gray-400">
            Combined Total
          </Typography>
          <Typography variant="h5" className="font-semibold">
            {totalCombined}
          </Typography>
        </Paper>
        <Paper className="p-4 shadow-md rounded-xl">
          <Typography variant="body2" className="text-gray-500 dark:text-gray-400">
            Diagnosis Categories
          </Typography>
          <Typography variant="h5" className="font-semibold">
            {diseaseEntries.length}
          </Typography>
        </Paper>
        <Paper className="p-4 shadow-md rounded-xl">
          <Typography variant="body2" className="text-gray-500 dark:text-gray-400">
            Centres Reporting
          </Typography>
          <Typography variant="h5" className="font-semibold">
            {centreChartEntries.length}
          </Typography>
        </Paper>
      </Box>

      <Paper className="p-4 md:p-5 shadow-md rounded-xl mb-6">
        <Box className="flex flex-col gap-4">
          <Box className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
            <Box className="lg:max-w-[42%]">
              <Typography variant="h6">Export Census</Typography>
              <Typography variant="body2" className="text-gray-500 dark:text-gray-400">
                Reporting/export controls for pilot and routine use. Keep legacy data and detailed OPD/activity lists optional.
              </Typography>
            </Box>

            <Box className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 w-full lg:max-w-[760px]">
              <TextField
                fullWidth
                size="small"
                select
                label="Export Format"
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
              >
                {EXPORT_FORMAT_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                size="small"
                select
                label="Export Scope"
                value={exportScope}
                onChange={(e) => setExportScope(e.target.value)}
              >
                {EXPORT_SCOPE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                size="small"
                select
                label="Detail Range"
                value={exportDetailRange}
                onChange={(e) => setExportDetailRange(e.target.value)}
                disabled={exportScope !== 'Full Census' || !includeDetailedExport}
              >
                {EXPORT_DETAIL_RANGE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                onClick={handleExport}
                className="min-h-[40px] whitespace-nowrap"
              >
                {exportFormat === 'Print / PDF' ? 'Open Print / PDF' : 'Download'}
              </Button>
            </Box>
          </Box>

          <Box className="flex flex-col gap-1 rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2">
            <Typography variant="caption" className="text-gray-500 dark:text-gray-400">
              Pilot display and export controls
            </Typography>
            <Box className="flex flex-col gap-1 xl:flex-row xl:flex-wrap xl:items-center xl:gap-x-6">
              <FormControlLabel
                className="m-0"
                control={
                  <Switch
                    checked={showLegacyData}
                    onChange={(e) => setShowLegacyData(e.target.checked)}
                    color="warning"
                  />
                }
                label="Show legacy data"
              />
              <FormControlLabel
                className="m-0"
                control={
                  <Switch
                    checked={includeDetailedExport}
                    onChange={(e) => setIncludeDetailedExport(e.target.checked)}
                    color="warning"
                  />
                }
                label="Include OPD/activity detail in export"
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {!hasAnyData ? (
        <Box className="mt-6 p-6 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-center">
          <Typography>No visit or activity data yet for the selected date range / centre.</Typography>
        </Box>
      ) : (
        <>
          <Paper className="p-4 shadow-md rounded-xl overflow-x-auto mb-6">
            {tablePaperTitle(
              'Combined Summary Table',
              'Separate and combined display of visits and activities'
            )}
            <table className="min-w-full table-auto border-collapse border border-gray-300 dark:border-gray-600">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className="border p-2 text-left">Category</th>
                  <th className="border p-2 text-left">Bucket</th>
                  <th className="border p-2 text-left">Count</th>
                </tr>
              </thead>
              <tbody>
                {combinedSummaryRows.map((row, idx) => (
                  <tr key={`${row.category}-${row.bucket}-${idx}`} className="text-sm">
                    <td className="border px-2 py-1">{row.category}</td>
                    <td className="border px-2 py-1">{row.bucket}</td>
                    <td className="border px-2 py-1">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Paper>

          <Box className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <MetricChartCard
              title="Disease Mix"
              subtitle="Top diagnosis / complaint categories in the selected filter"
              mode="bar"
              data={diseaseData}
              options={diseaseBarOptions}
              entries={diseaseChartEntries}
              compactLabel="Dominant diagnosis category"
            />

            <MetricChartCard
              title="Sex Distribution"
              subtitle="Citizen sex split for the selected filter"
              mode="pie"
              data={sexData}
              options={pieOptions}
              entries={sexChartEntries}
              compactLabel="Single sex category"
            />

            <MetricChartCard
              title="Age Group Distribution"
              subtitle="Age profile of OPD visits"
              mode="bar"
              data={ageData}
              options={ageBarOptions}
              entries={ageChartEntries}
              compactLabel="Dominant age group"
            />

            <MetricChartCard
              title="Visit Type Distribution"
              subtitle="Visit type mix for the selected date range"
              mode="bar"
              data={visitTypeData}
              options={visitTypeBarOptions}
              entries={visitTypeChartEntries}
              compactLabel="Single visit type"
            />

            <MetricChartCard
              title="Activity Type Distribution"
              subtitle="Most common guided / intern activity types"
              mode="bar"
              data={activityTypeData}
              options={activityTypeBarOptions}
              entries={activityTypeChartEntries}
              compactLabel="Single activity type"
            />

            <MetricChartCard
              title="Activity Status Distribution"
              subtitle="Draft / approved / returned split"
              mode="pie"
              data={activityStatusData}
              options={pieOptions}
              entries={activityStatusChartEntries}
              compactLabel="Single activity status"
            />

            <Box className="md:col-span-2">
              <MetricChartCard
                title="Centre-wise Combined Distribution"
                subtitle={showLegacyData ? "Legacy / unmatched early-stage records are included in this view" : "Pilot view: official 4-centre reporting only; legacy records are hidden unless toggled on"}
                mode="bar"
                data={centreData}
                options={centreBarOptions}
                entries={centreChartEntries}
                compactLabel="Single reporting centre"
              />
            </Box>
          </Box>

          <Box className="grid grid-cols-1 gap-6 my-6">
            <BasicSummaryTable
              title="Year-wise Centre OPD Summary"
              subtitle="Historical style centre totals built from currently filtered live OPD visits"
              columns={yearWiseCentreColumns}
              rows={yearWiseCentreRows}
            />
            <BasicSummaryTable
              title="Centre-wise Age Distribution"
              subtitle="Infant / child / adolescent / adult / elderly distribution by centre"
              columns={centreAgeColumns}
              rows={centreAgeRows}
            />
            <BasicSummaryTable
              title="Centre-wise Sex Summary"
              subtitle="Male / female / other summary by centre"
              columns={[
                { key: 'centre', label: 'Centre' },
                { key: 'Male', label: 'Male' },
                { key: 'Female', label: 'Female' },
                { key: 'Other', label: 'Other' },
                { key: 'Unknown', label: 'Unknown' },
                { key: 'total', label: 'Total' },
              ]}
              rows={centreSexRows}
            />
            <BasicSummaryTable
              title="Centre-wise NCD Summary"
              subtitle="Reference-style NCD grouping derived from current diagnosis text"
              columns={[
                { key: 'centre', label: 'Centre' },
                { key: 'DM', label: 'DM' },
                { key: 'HTN', label: 'HTN' },
                { key: 'DM + HTN', label: 'DM + HTN' },
                { key: 'Thyroid', label: 'Thyroid' },
                { key: 'COPD / Asthma', label: 'COPD / Asthma' },
                { key: 'Others', label: 'Others' },
                { key: 'total', label: 'Total' },
              ]}
              rows={centreNcdRows}
            />
            <BasicSummaryTable
              title="Centre-wise Clinical / Service Category Summary"
              subtitle="Reference-style respiratory / GI / skin / animal bite / ANC / PNC grouping"
              columns={[
                { key: 'centre', label: 'Centre' },
                { key: 'Respiratory', label: 'Respiratory' },
                { key: 'Gastrointestinal', label: 'Gastrointestinal' },
                { key: 'Skin', label: 'Skin' },
                { key: 'Animal Bite', label: 'Animal Bite' },
                { key: 'ANC', label: 'ANC' },
                { key: 'PNC', label: 'PNC' },
                { key: 'Others', label: 'Others' },
                { key: 'total', label: 'Total' },
              ]}
              rows={centreServiceRows}
            />
          </Box>

          <Box className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <Paper className="p-4 shadow-md rounded-xl overflow-x-auto">
              {tablePaperTitle('OPD Visits Table', 'OPD visits within selected date range')}
              <table className="min-w-full table-auto border-collapse border border-gray-300 dark:border-gray-600">
                <thead>
                  <tr className="bg-gray-100 dark:bg-gray-700">
                    <th className="border p-2 text-left">Date</th>
                    <th className="border p-2 text-left">Visit ID</th>
                    <th className="border p-2 text-left">Citizen</th>
                    <th className="border p-2 text-left">Diagnosis / Complaint</th>
                    <th className="border p-2 text-left">Visit Type</th>
                    <th className="border p-2 text-left">Centre</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedVisitRows.length ? (
                    paginatedVisitRows.map((visit, idx) => (
                      <tr key={visit?._id || visit?.visitId || idx} className="text-sm">
                        <td className="border px-2 py-1">{toLocalYmd(extractVisitDate(visit)) || '-'}</td>
                        <td className="border px-2 py-1">{visit?.visitId || visit?._id || '-'}</td>
                        <td className="border px-2 py-1">
                          {extractPersonLabel(visit)} ({extractPersonId(visit)})
                        </td>
                        <td className="border px-2 py-1">{extractDiseaseLabel(visit)}</td>
                        <td className="border px-2 py-1">{normalizeVisitType(visit?.visitType || visit?.type)}</td>
                        <td className="border px-2 py-1">
                          {normalizeCentre(visit?.centre || visit?.healthCentre || visit?.location)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="border px-2 py-3 text-center" colSpan={6}>
                        No OPD visits found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <TablePaginationBar
                page={visitPage}
                totalPages={visitTotalPages}
                onPrev={() => setVisitPage((prev) => Math.max(1, prev - 1))}
                onNext={() => setVisitPage((prev) => Math.min(visitTotalPages, prev + 1))}
                totalRows={pilotVisitRows.length}
                pageSize={DEFAULT_TABLE_PAGE_SIZE}
                label="OPD visits"
              />
            </Paper>

            <Paper className="p-4 shadow-md rounded-xl overflow-x-auto">
              {tablePaperTitle('Intern / Guided Activities Table', 'Activities within selected date range')}
              <table className="min-w-full table-auto border-collapse border border-gray-300 dark:border-gray-600">
                <thead>
                  <tr className="bg-gray-100 dark:bg-gray-700">
                    <th className="border p-2 text-left">Date</th>
                    <th className="border p-2 text-left">Activity</th>
                    <th className="border p-2 text-left">Status</th>
                    <th className="border p-2 text-left">Citizen / Context</th>
                    <th className="border p-2 text-left">Done By</th>
                    <th className="border p-2 text-left">Centre</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedActivityRows.length ? (
                    paginatedActivityRows.map((item, idx) => (
                      <tr key={item?._id || item?.id || idx} className="text-sm">
                        <td className="border px-2 py-1">{toLocalYmd(extractActivityDate(item)) || '-'}</td>
                        <td className="border px-2 py-1">{normalizeActivityType(item)}</td>
                        <td className="border px-2 py-1">{normalizeActivityStatus(item)}</td>
                        <td className="border px-2 py-1">
                          {extractPersonLabel(item)} ({extractPersonId(item)})
                        </td>
                        <td className="border px-2 py-1">{extractCreatedBy(item)}</td>
                        <td className="border px-2 py-1">
                          {normalizeCentre(item?.centre || item?.healthCentre || item?.location || item?.site)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="border px-2 py-3 text-center" colSpan={6}>
                        No intern or guided activities found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <TablePaginationBar
                page={activityPage}
                totalPages={activityTotalPages}
                onPrev={() => setActivityPage((prev) => Math.max(1, prev - 1))}
                onNext={() => setActivityPage((prev) => Math.min(activityTotalPages, prev + 1))}
                totalRows={pilotActivityRows.length}
                pageSize={DEFAULT_TABLE_PAGE_SIZE}
                label="Activities"
              />
            </Paper>
          </Box>
        </>
      )}
    </Box>
  );
};

export default DailyOPDCensus;
